// src/hooks/usePatientFilters.js
import { useState, useMemo } from 'react';

// Helper de idade (Fallback apenas caso a View não mande a coluna 'idade')
const calculateAge = (dtNascimento) => {
    if (!dtNascimento) return 0;
    const birthDate = new Date(dtNascimento);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
    }
    return age;
};

export function usePatientFilters(
    initialPatients, 
    searchKeys = ['nome', 'cns', 'endereco'], 
    defaultSort = { key: 'nome', direction: 'ascending' },
    matchAllAlerts = true // true = Interseção 'E' (Restritivo), false = União 'OU' (Inclusivo)
) {
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState(defaultSort);
    const [activeAlerts, setActiveAlerts] = useState([]); 
    const [selectedMicroArea, setSelectedMicroArea] = useState('');
    const [statusFilter, setStatusFilter] = useState({ key: '', type: '' }); 

    const filteredList = useMemo(() => {
        let result = [...initialPatients];

        // 1. Filtro de Texto (Busca Genérica)
        if (searchTerm) {
            const lowerTerm = searchTerm.toLowerCase();
            result = result.filter(p => 
                searchKeys.some(key => {
                    const val = p[key] || ''; 
                    return String(val).toLowerCase().includes(lowerTerm);
                })
            );
        }

        // 2. Filtro de Condições Clínicas e Faixas Etárias (Arquitetura V2)
        if (activeAlerts.length > 0) {
            result = result.filter(p => {
                const checks = activeAlerts.map(alert => {
                    // Mapeamento Booleano Direto (V2)
                    if (alert === 'DM') return p.is_diabetico === true;
                    if (alert === 'HAS') return p.is_hipertenso === true;
                    if (alert === 'Gestante') return p.is_gestante === true;
                    if (alert === 'Acamado') return p.is_acamado === true;
                    if (alert === 'Domiciliado') return p.is_domiciliado === true;
                    if (alert === 'Acamado,Domiciliado') return p.is_acamado === true || p.is_domiciliado === true;

                    // Avaliação de Idades e Faixas Etárias (V2 + Fallback)
                    const idade = (p.idade !== undefined && p.idade !== -1) ? p.idade : calculateAge(p.dt_nascimento);

                    if (alert === 'Idoso') return idade >= 60;
                    if (alert === 'Idade_9_14') return idade >= 9 && idade <= 14;
                    if (alert === 'Idade_14_69') return idade >= 14 && idade <= 69;
                    if (alert === 'Idade_25_64') return idade >= 25 && idade <= 64;
                    if (alert === 'Idade_50_69') return idade >= 50 && idade <= 69;
                    
                    return false;
                });

                return matchAllAlerts ? checks.every(Boolean) : checks.some(Boolean);
            });
        }

        // 3. Filtro de Micro-área
        if (selectedMicroArea) {
            result = result.filter(p => String(p.micro_area) === String(selectedMicroArea));
        }

        // 4. Filtro de Status Inteligente (Bolinhas do Front)
        if (statusFilter.key && statusFilter.type) {
            result = result.filter(p => {
                const statusObj = p.status?.[statusFilter.key];
                if (!statusObj) return false;
                
                const statusString = (typeof statusObj === 'object' && statusObj.status) ? statusObj.status : statusObj;

                if (statusFilter.type === 'pending') return statusString === 'critical' || statusString === 'warning';
                if (statusFilter.type === 'completed') return statusString === 'ok';
                return true;
            });
        }

        // 5. Ordenação Lógica e Alfanumérica
        if (sortConfig.key) {
            result.sort((a, b) => {
                let aValue = a[sortConfig.key];
                let bValue = b[sortConfig.key];

                if (sortConfig.key === 'score') {
                    aValue = a.status?.score || 0;
                    bValue = b.status?.score || 0;
                }
                
                if (sortConfig.key === 'dt_nascimento' || sortConfig.key === 'idade') {
                    const ageA = (a.idade !== undefined && a.idade !== -1) ? a.idade : calculateAge(a.dt_nascimento);
                    const ageB = (b.idade !== undefined && b.idade !== -1) ? b.idade : calculateAge(b.dt_nascimento);
                    return sortConfig.direction === 'ascending' ? ageA - ageB : ageB - ageA;
                }
                
                if (typeof aValue === 'string') aValue = aValue.toLowerCase();
                if (typeof bValue === 'string') bValue = bValue.toLowerCase();

                // Regra específica para ordenar Endereços respeitando o número da casa
                if (sortConfig.key === 'endereco') {
                    const ruaA = (a.endereco || '').toLowerCase();
                    const ruaB = (b.endereco || '').toLowerCase();
                    if (ruaA !== ruaB) return ruaA < ruaB ? (sortConfig.direction === 'ascending' ? -1 : 1) : (sortConfig.direction === 'ascending' ? 1 : -1);
                    
                    const numA = parseInt(a.numero) || 0;
                    const numB = parseInt(b.numero) || 0;
                    return sortConfig.direction === 'ascending' ? numA - numB : numB - numA;
                }

                if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
                if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
                return 0;
            });
        }

        return result;
    }, [initialPatients, searchTerm, searchKeys, sortConfig, activeAlerts, selectedMicroArea, statusFilter, matchAllAlerts]);

    const toggleStatusFilter = (columnKey) => {
        setStatusFilter(prev => {
            if (prev.key !== columnKey) return { key: columnKey, type: 'pending' };
            if (prev.type === 'pending') return { key: columnKey, type: 'completed' };
            return { key: '', type: '' };
        });
    };

    return {
        searchTerm, setSearchTerm,
        sortConfig, setSortConfig,
        activeAlerts, setActiveAlerts,
        selectedMicroArea, setSelectedMicroArea,
        statusFilter, setStatusFilter: toggleStatusFilter,
        filteredList
    };
}